import { create } from 'zustand'
import { useShallow } from 'zustand/shallow'

/** A single driver's failure from a batch business-trip generation. */
export type BusinessTripFailure = {
  code?: string
  message?: string
}

/** Company + period the failure set belongs to. Markers from another company/period are stale. */
export type BusinessTripFailuresScope = {
  companyId: string
  from: string // YYYY-MM-DD
  to: string // YYYY-MM-DD
}

type State = {
  scope: BusinessTripFailuresScope | null
  /** Keyed by driverId (as string). */
  failures: Record<string, BusinessTripFailure>
}

type Actions = {
  setFailures: (
    scope: BusinessTripFailuresScope,
    failures: Record<string, BusinessTripFailure>,
  ) => void
  clearFailures: () => void
}

/**
 * In-memory only (NOT persisted). Markers reflect the result of the last
 * generation for the current session; a refresh clears them, and a new
 * generation overwrites them — so they never contradict data the user has since
 * changed (e.g. adding a contract that was previously missing).
 */
export const useBusinessTripFailuresStore = create<State & Actions>((set) => ({
  scope: null,
  failures: {},

  setFailures: (scope, failures) => set({ scope, failures }),

  clearFailures: () => set({ scope: null, failures: {} }),
}))

/**
 * Returns the failure for `driverId` only when the stored scope matches the
 * given company + period; otherwise the stored markers are stale and ignored.
 */
export const useBusinessTripFailuresForScope = (
  scope: BusinessTripFailuresScope,
) =>
  useBusinessTripFailuresStore(
    useShallow((s) => {
      const matches =
        s.scope !== null &&
        s.scope.companyId === scope.companyId &&
        s.scope.from === scope.from &&
        s.scope.to === scope.to
      return matches ? s.failures : EMPTY_FAILURES
    }),
  )

const EMPTY_FAILURES: Record<string, BusinessTripFailure> = {}
